import { useMemo, useRef } from "react";
import { observer } from "mobx-react-lite";

import { BooleanIcon, getBooleanColors } from "../../primitives/utils";

import {
  useGridContext,
  ViewMode,
} from "@/components/Evaluate/TableComponents/hooks/useEvaluateGridContext";
import { BasicTooltip, InfoTooltipContent, InfoTooltipSection } from "@/components/ui/Tooltip";

interface AssertionsCellProps {
  prompt?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  isColumnSelected?: boolean;
}

export const AssertionsCell = observer(
  ({ prompt = "", value, isColumnSelected = false }: AssertionsCellProps) => {
    const { tableStore } = useGridContext();
    const isCompactMode = tableStore?.viewMode === ViewMode.COMPACT;
    const scrollRef = useRef<HTMLDivElement>(null);

    const assertions = useMemo(() => {
      if (typeof value === "boolean") {
        return [[prompt, { value, reasoning: null, citation: null }]];
      } else if (typeof value === "object" && value !== null) {
        if ("value" in value && typeof value.value === "boolean") {
          return [[prompt, value]];
        }
        return Object.entries(value).map(([key, val]) => {
          if (typeof val === "boolean") {
            return [key, { value: val, reasoning: null, citation: null }];
          } else if (typeof val === "object" && val !== null && "value" in val) {
            return [key, val];
          }
          return [key, { value: val, reasoning: null, citation: null }];
        });
      }
      return [];
    }, [value, prompt]);

    if (assertions.length === 0) {
      return (
        <div>
          <span className="text-gray-500">No assertions were provided for this column.</span>
        </div>
      );
    }

    return (
      <div
        ref={scrollRef}
        className={`grid h-full w-full overflow-x-auto ${
          isCompactMode ? "overflow-y-hidden" : "overflow-y-auto"
        }`}
        style={{
          gridTemplateColumns: `repeat(${assertions.length}, ${
            isCompactMode ? "minmax(24px, 1fr)" : "minmax(36px, 1fr)"
          })`,
        }}
      >
        {assertions.map(([key, assertionData]) => {
          const booleanValue = assertionData.value;
          const { reasoning } = assertionData;
          const { citation } = assertionData;

          const sections: InfoTooltipSection[] = [
            {
              key: "assertion",
              title: "Assertion",
              items: [
                {
                  key: "assertion-text",
                  label: "",
                  value: key,
                  className: "text-gray-700 whitespace-pre-wrap",
                },
              ],
            },
          ];

          if (reasoning) {
            sections.push({
              key: "reasoning",
              title: "Reasoning",
              items: [
                {
                  key: "reasoning-value",
                  label: "",
                  value: reasoning,
                  className: "text-gray-600 whitespace-pre-wrap",
                },
              ],
            });
          }

          if (citation) {
            sections.push({
              key: "citation",
              title: "Citation",
              items: [
                {
                  key: "citation-value",
                  label: "",
                  value: citation,
                  className: "italic text-gray-500 whitespace-pre-wrap",
                },
              ],
            });
          }

          const tooltipContent = (
            <InfoTooltipContent sections={sections} className="min-w-[200px] max-w-[320px]" />
          );

          const colorClasses = getBooleanColors(booleanValue);

          return (
            <span
              key={key}
              className={`flex h-full items-center justify-center ${colorClasses} ${
                booleanValue ? "border-r border-green-200" : "border-r border-red-200"
              } ${isColumnSelected ? "!border-blue-200 !bg-blue-50" : ""}`}
            >
              <BasicTooltip content={tooltipContent} disabled={!key} side="top" variant="white">
                <span>
                  <BooleanIcon value={booleanValue} />
                </span>
              </BasicTooltip>
            </span>
          );
        })}
      </div>
    );
  },
);

export type { AssertionsCellProps };
